import type { ReactNode } from "react";
import { Box, ListItem, ListItemAvatar, ListItemText } from "@mui/material";

export type AdminListRowProps = {
  avatar: ReactNode;
  primary: ReactNode;
  secondary: ReactNode;
  actions: ReactNode;
  divider?: boolean;
};

/**
 * Avatar + text + actions row shared by the admin list views.
 *
 * The actions deliberately sit in normal flow rather than in MUI's
 * `secondaryAction` slot: that slot is absolutely positioned, so the row has
 * to reserve a fixed strip for it, and any row whose text needs the full
 * width — or that carries more buttons than the strip fits — ends up with the
 * two overlapping. In flow they simply wrap under the text when space runs
 * out, which is what happens at phone width.
 */
export const AdminListRow = ({
  avatar,
  primary,
  secondary,
  actions,
  divider = true,
}: AdminListRowProps) => (
  <ListItem
    divider={divider}
    sx={{ flexWrap: "wrap", alignItems: "flex-start", gap: 1 }}
  >
    <ListItemAvatar>{avatar}</ListItemAvatar>
    <ListItemText
      primary={primary}
      secondary={secondary}
      sx={{ flex: "1 1 12rem", minWidth: 0, my: 0 }}
      slotProps={{
        primary: { component: "div" },
        secondary: { component: "div" },
      }}
    />
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "flex-end",
        ml: "auto",
      }}
    >
      {actions}
    </Box>
  </ListItem>
);
